/**
 * ABET DASHBOARD - MODULAR TEMPLATE
 * Created: 2025-12-23
 * Updated: 2025-12-23 - Changed layout: nav rows below header, not sidebar
 *
 * Original App.jsx preserved at: E:\abet\dashboard\src\App.jsx
 */

import { useState, useEffect, Suspense, lazy } from 'react'
import './App_template.css'

// ============================================
// STANDALONE COMPONENTS (uncomment as created)
// ============================================
import HamburgerMenu from './components/HamburgerMenu'
import Logo from './components/Logo'
import CalculatorButton from './components/CalculatorButton'
import CoffeeButton from './components/CoffeeButton'
import SocialsDropdown from './components/SocialsDropdown'
import UserMenuButton from './components/UserMenuButton'
import { PreferencesProvider } from './components/UserPreferences/PreferencesContext'
import { useWebSocket } from '../hooks/useWebSocket'
import { useAuthStore } from '../stores/authStore'
import { useOddsStore } from '../stores/oddsStore'
import SportNav from './components/SportNav'
import ModuleButton from './components/ModuleButton'
import MarketNav from './components/MarketNav'
import DateNav from './components/DateNav'
import MainContent from './components/MainContent'
import ScrollButtons from './components/ScrollButtons'

// ============================================
// EXISTING COMPONENTS (from main dashboard)
// ============================================
import CalculatorModal from '../components/CalculatorModal'
import AuthModal from '../components/AuthModal'

function App() {
  // ============================================
  // DATA CONNECTION - WebSocket for live odds
  // ============================================
  useWebSocket() // Establishes connection, populates oddsStore

  // ============================================
  // GLOBAL DATA LOADER - Fetch ALL odds on mount + periodic sync
  // This makes sport switching INSTANT (just filter, no fetch)
  // Periodic sync every 5 minutes removes stale/finished games
  // ============================================
  const batchUpdateOdds = useOddsStore((state) => state.batchUpdateOdds)
  const clearOdds = useOddsStore((state) => state.clearOdds)

  useEffect(() => {
    const loadAllOdds = async (isSync = false) => {
      const logPrefix = isSync ? '[App Sync]' : '[App]'
      console.log(`${logPrefix} Loading ALL odds data...`)
      try {
        const res = await fetch('/api/odds')
        if (!res.ok) throw new Error('HTTP ' + res.status)
        const data = await res.json()
        // On sync: clear stale data before loading fresh
        if (isSync) clearOdds()
        batchUpdateOdds(data)
        console.log(`${logPrefix} Loaded ${data.length} odds${isSync ? ' - stale games cleared' : ''}`)
      } catch (err) {
        console.error(`${logPrefix} Failed to load odds:`, err)
      }
    }

    // Initial load
    loadAllOdds(false)

    // Periodic sync every 5 minutes (300000ms) to remove stale/finished games
    // This clears the store before loading to ensure finished games are removed
    const syncInterval = setInterval(() => loadAllOdds(true), 300000)

    return () => clearInterval(syncInterval)
  }, [batchUpdateOdds, clearOdds])

  // ============================================
  // AUTH INITIALIZATION
  // ============================================
  const initialize = useAuthStore((state) => state.initialize)
  const user = useAuthStore((state) => state.user)

  useEffect(() => {
    initialize()
  }, [initialize])

  // ============================================
  // CORE STATE (passed to child components)
  // ============================================
  const [selectedModule, setSelectedModule] = useState('Landing')
  const [selectedSport, setSelectedSport] = useState('NFL')
  const [selectedMarket, setSelectedMarket] = useState('Moneyline')
  const [selectedDate, setSelectedDate] = useState(new Date())  // Default to today - always show ESPN schedule

  // Collapse states for nav rows
  const [sportNavCollapsed, setSportNavCollapsed] = useState(false)
  const [marketNavCollapsed, setMarketNavCollapsed] = useState(false)
  const [dateNavCollapsed, setDateNavCollapsed] = useState(false)

  // Live/Today filter states
  const [isLiveActive, setIsLiveActive] = useState(false)
  const [isTodayActive, setIsTodayActive] = useState(false)

  // Modal states
  const [calculatorOpen, setCalculatorOpen] = useState(false)
  const [authModalOpen, setAuthModalOpen] = useState(false)

  // Modules that need sport navigation
  const SPORTS_MODULES = ['Odds', 'Stats', 'Arbitrage', 'Middles', 'PositiveEV', '+EV', 'Models']
  const showSportsNav = SPORTS_MODULES.includes(selectedModule)

  // Only Odds module needs date navigation
  const DATE_MODULES = ['Odds']
  const showDateNav = DATE_MODULES.includes(selectedModule)

  // Reset Live/Today filters when leaving Odds module
  useEffect(() => {
    if (selectedModule !== 'Odds') {
      setIsLiveActive(false)
      setIsTodayActive(false)
    }
  }, [selectedModule])

  // Handle Live button click
  const handleLiveClick = () => {
    setIsLiveActive(!isLiveActive)
    setIsTodayActive(false) // Deactivate Today when Live is clicked
  }

  // Handle Today button click
  const handleTodayClick = () => {
    setIsTodayActive(!isTodayActive)
    setIsLiveActive(false) // Deactivate Live when Today is clicked
    setSelectedDate(new Date()) // Set date to today
  }

  return (
    <PreferencesProvider>
    <div className="app">
      {/* ========================================
          HEADER
          ======================================== */}
      <header className="header">
        <div className="header-left">
          {/* [1] HAMBURGER MENU */}
          <HamburgerMenu
            selectedModule={selectedModule}
            onModuleSelect={setSelectedModule}
          />

          {/* [2] LOGO - Click to go to Landing Page */}
          <Logo onLogoClick={() => setSelectedModule('Landing')} />
        </div>

        <div className="header-right">
          {/* [3] MODULE BUTTON - Hidden on Landing page */}
          {selectedModule !== 'Landing' && (
            <ModuleButton
              selectedModule={selectedModule}
              onClick={() => console.log('Open module selector')}
            />
          )}

          {/* [3a] LOGIN BUTTON - Only on Landing page when not logged in */}
          {selectedModule === 'Landing' && !user && (
            <button
              className="header-login-btn"
              onClick={() => setAuthModalOpen(true)}
            >
              <i className="fas fa-sign-in-alt"></i>
              <span>Login / Sign Up</span>
            </button>
          )}

          {/* [4] CALCULATOR BUTTON */}
          <CalculatorButton onClick={() => setCalculatorOpen(true)} />

          {/* [4] COFFEE BUTTON */}
          <CoffeeButton />

          {/* [5] SOCIALS DROPDOWN */}
          <SocialsDropdown />

          {/* [6] USER PREFERENCES */}
          <UserMenuButton />
        </div>
      </header>

      {/* ========================================
          NAV ROW 1: SPORTS (collapsible)
          Only show for sports-related modules
          ======================================== */}
      {showSportsNav && (
        <SportNav
          selectedSport={selectedSport}
          onSportSelect={setSelectedSport}
          collapsed={sportNavCollapsed}
          onToggleCollapse={() => setSportNavCollapsed(!sportNavCollapsed)}
          onLiveClick={handleLiveClick}
          onTodayClick={handleTodayClick}
          isLiveActive={isLiveActive}
          isTodayActive={isTodayActive}
          selectedModule={selectedModule}
        />
      )}

      {/* ========================================
          NAV ROW 2: MARKETS (collapsible)
          Only show for sports-related modules
          ======================================== */}
      {showSportsNav && (
        <MarketNav
          selectedModule={selectedModule}
          selectedMarket={selectedMarket}
          onMarketSelect={setSelectedMarket}
          collapsed={marketNavCollapsed}
          onToggleCollapse={() => setMarketNavCollapsed(!marketNavCollapsed)}
        />
      )}

      {/* ========================================
          NAV ROW 3: DATE (collapsible)
          Only show for date-relevant modules (not Stats)
          ======================================== */}
      {showDateNav && (
        <DateNav
          selectedDate={selectedDate}
          onDateSelect={setSelectedDate}
          collapsed={dateNavCollapsed}
          onToggleCollapse={() => setDateNavCollapsed(!dateNavCollapsed)}
        />
      )}

      {/* ========================================
          MAIN CONTENT
          ======================================== */}
      <MainContent
        selectedModule={selectedModule}
        selectedSport={selectedSport}
        selectedMarket={selectedMarket}
        selectedDate={selectedDate}
        onVisibleDateChange={setSelectedDate}
        onModuleSelect={setSelectedModule}
        isLiveActive={isLiveActive}
        isTodayActive={isTodayActive}
        onAuthClick={() => setAuthModalOpen(true)}
        user={user}
      />

      {/* ========================================
          MODALS
          ======================================== */}
      <CalculatorModal
        isOpen={calculatorOpen}
        onClose={() => setCalculatorOpen(false)}
      />

      <AuthModal
        isOpen={authModalOpen}
        onClose={() => setAuthModalOpen(false)}
      />

      {/* ======================================== SCROLL BUTTONS ======================================== */}
      <ScrollButtons
      />

    </div>
    </PreferencesProvider>
  )
}

export default App
